import { createElement, useEffect, useRef, FC } from 'react';
import { RawFontContainer, DefaultFonts } from 'legos/typography';
import { FormSpy } from 'react-final-form';
import { useToastTheme } from './toast-notification.theme';
import {
  ToastFrame,
  ToastContainer as RawToastContainer,
  Text,
  Button,
  VisuallyHidden
} from './toast-notification.ccm.css';
import { useToast } from './context';

/**
 * Properties that are shared by every type of notification
 */
export interface SharedToastProps {
  /** Text to be displayed in the notifications */
  text: string;
  /**
   * Time in ms to keep the toast alive
   * @default 3500
   */
  timeout?: number;
  /** Optional function to run immediately after expiring */
  onComplete?: () => void;
}

export interface InformationalToastProps extends SharedToastProps {
  /** actionlabel is always undefined for informational toasts */
  actionText?: undefined;
  /** onAction is always undefined for informational toasts */
  onAction?: undefined;
}

export interface InteractableToastProps extends SharedToastProps {
  /** Action button text */
  actionText: string;
  /** Callback for interacting with the button */
  onAction: () => void;
}

/**
 * Toasts can either be informational or Interactable.
 * If actionText or onAction is defined, both will be required
 */
export type ToastProps = InformationalToastProps | InteractableToastProps;

/**
 * Props that we use internally but don't want to expose in the public API
 */
export type InternalToastProps = ToastProps & {
  animationDuration?: number;
  expired?: boolean;
};

const ToastContainer = RawFontContainer.as(RawToastContainer.div);

export const InternalToast: FC<InternalToastProps> = ({
  onAction,
  actionText,
  text,
  expired,
  animationDuration
}) => {
  const { backgroundColor, textColor, buttonColor } = useToastTheme();

  return (
    <ToastFrame.div>
      <ToastContainer
        $isHiding={expired}
        $animationDuration={`${animationDuration || 0}ms`}
        $backgroundColor={backgroundColor}
        $color={textColor}
        $fontFace={DefaultFonts}
      >
        <Text.span
          dangerouslySetInnerHTML={{
            __html: text
          }}
        />
        <VisuallyHidden.span role="alert" aria-live="assertive">
          New Notification: {text}
        </VisuallyHidden.span>
        {actionText && onAction && (
          <Button.button $buttonColor={buttonColor} onClick={onAction}>
            {actionText}
          </Button.button>
        )}
      </ToastContainer>
    </ToastFrame.div>
  );
};

// function for auto-incrementing ids
let nextId = 0;
const generateID = () => nextId++;

export const Toast: FC<ToastProps> = ({ timeout, onComplete, ...props }) => {
  const { dispatch } = useToast();
  const id = useRef(generateID());

  // the exhaustive-deps rule is disabled because we only
  // want to run this function on mount.
  useEffect(() => {
    dispatch({
      render: (providerProps) => (
        <InternalToast key={id.current} {...props} {...providerProps} />
      ),
      timeout,
      onComplete
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export const FormSubmitSuccessToast: FC<ToastProps> = (props) => (
  <FormSpy subscription={{ submitSucceeded: true }}>
    {({ submitSucceeded }) => submitSucceeded && <Toast {...props} />}
  </FormSpy>
);
